import { inject, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import type { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { AntiAuthGuard } from './bootstrap/guards/anti-auth.guard';
import { AuthGuard } from './bootstrap/guards/auth.guard';
import { PluginRegistry } from '@tourfold/shared-kernels/plugin-engine/web';

import { TenantFoundGuard } from './bootstrap/guards/tenant-found.guard';
import { provideSettingsElementGeneralSettings } from '@tourfold/features/settings/general';
import { provideSettingsElementAuditLogSettings } from '@tourfold/features/settings/audit-log';
import { provideSettingsElementAccess } from '@tourfold/features/settings/access';
import { provideSettingsElementSmsGateway } from '@tourfold/features/settings/sms';
import { provideSettingsElementSkills } from '@tourfold/features/settings/skills';
import { provideSettingsElementMeasureUnits } from '@tourfold/features/settings/measure-units';
import { provideSettingsElementWebhooks } from '@tourfold/features/settings/webhooks';
import { provideSettingsElementGeocoding } from '@tourfold/features/settings/geocoding';
import { provideSettingsElementMcpServer } from '@tourfold/features/settings/mcp';
import { provideSettingsElementObjects } from '@tourfold/features/custom-objects/settings';
import { provideSettingsElementPushNotifications } from './general/settings-push-notifications/settings-push-notifications.provider';
import { provideSettingsElementDevices } from '@tourfold/features/devices';
import { provideSettingsElementAreas } from '@tourfold/features/settings/areas';
import { provideSettingsElementBrands } from '@tourfold/features/settings/brands';
import { provideSettingsElementCustomDomains } from '@tourfold/features/settings/custom-domains';
import { provideSettingsElementTags } from '@tourfold/features/settings/tags';
import { provideSettingsElementTheme } from '@tourfold/features/settings/theme';
import { provideSettingsElementAiAssistant } from '@tourfold/features/ai-assistant';
import { provideSettingsElementBilling } from '@tourfold/features/settings/billing';
import { provideSettingsElementDocumentManagement } from '@tourfold/features/document-management';
import { provideSettingsElementUserProfile } from './general/settings-user-profile/settings-user-profile.provider';

/**
 * Factory that produces a fresh Routes array.
 *
 * The plugin registry is populated synchronously in `main.ts` during
 * APP_INITIALIZER (before any route activates), so route factories can
 * read from it directly without waiting on additional async setup.
 */
export function buildRootRoutes(): Routes {
  return [
    {
      path: '',
      loadComponent: () =>
        import('./public-pages/sso/auth-redirect.component').then((m) => m.AuthRedirectComponent),
    },
    { path: 'fnrl', redirectTo: 'app', pathMatch: 'prefix' },
    {
      path: 'app',
      loadChildren: () => {
        const pluginRegistry = inject(PluginRegistry);
        return import('./shell/app-routes').then((m) =>
          m.buildAppRoutes(
            pluginRegistry.getActiveAdditionalRoutes(),
            pluginRegistry.getActiveProviders(),
            pluginRegistry.getActiveRouteSlotOverrides()
          )
        );
      },
      canActivate: [AuthGuard],
    },
    {
      path: 'login',
      loadChildren: () =>
        import('./public-pages/legacy-login/login/login.module').then((m) => m.LoginModule),
      canActivate: [AntiAuthGuard],
    },
    {
      path: 'auth/keycloak/callback',
      loadComponent: () =>
        import('./public-pages/sso/keycloak-callback.component').then(
          (m) => m.KeycloakCallbackComponent
        ),
    },
    {
      path: 'tenant-not-found',
      loadChildren: () =>
        import('./public-pages/errors/tenant-not-found/tenant-not-found.module').then(
          (m) => m.TenantNotFoundModule
        ),
      canActivate: [TenantFoundGuard],
    },
    {
      path: 'cannot-reach-backend',
      loadComponent: () =>
        import('./public-pages/errors/cannot-reach-backend/cannot-reach-backend.component').then(
          (m) => m.CannotReachBackendComponent
        ),
    },
    {
      path: 'tenant-access-blocked',
      loadComponent: () =>
        import('./public-pages/errors/tenant-access-blocked/tenant-access-blocked.component').then(
          (m) => m.TenantAccessBlockedComponent
        ),
    },
    {
      path: 'reset-password',
      loadChildren: () =>
        import('./public-pages/legacy-login/reset-password/reset-password.module').then(
          (m) => m.ResetPasswordModule
        ),
      canActivate: [AntiAuthGuard],
    },
    {
      path: 'change-password',
      loadChildren: () =>
        import('./public-pages/legacy-login/change-password/change-password.module').then(
          (m) => m.ChangePasswordModule
        ),
      canActivate: [AntiAuthGuard],
    },
    {
      path: 'settings',
      loadChildren: () => import('./settings/settings.routes').then((m) => m.APP_ROUTES),
      canActivate: [AuthGuard],
      providers: [
        provideSettingsElementUserProfile(),
        provideSettingsElementGeneralSettings(),
        provideSettingsElementAuditLogSettings(),
        provideSettingsElementAccess(),
        provideSettingsElementSmsGateway(),
        provideSettingsElementSkills(),
        provideSettingsElementMeasureUnits(),
        provideSettingsElementAreas(),
        provideSettingsElementBrands(),
        provideSettingsElementCustomDomains(),
        provideSettingsElementTags(),
        provideSettingsElementAiAssistant(),
        provideSettingsElementBilling(),
        provideSettingsElementTheme(),
        provideSettingsElementWebhooks(),
        provideSettingsElementGeocoding(),
        provideSettingsElementPushNotifications(),
        provideSettingsElementDevices(),
        provideSettingsElementMcpServer(),
        provideSettingsElementDocumentManagement(),
        ...(environment.enableCustomObjects ? [provideSettingsElementObjects()] : []),
      ],
    },
    { path: '**', redirectTo: '/' },
  ];
}

@NgModule({
  imports: [RouterModule.forRoot(buildRootRoutes())],
  exports: [RouterModule],
})
export class AppRoutingModule {}
